import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Mail,
  Phone,
  Calendar,
  MapPin,
  Ban,
  CheckCircle,
  ArrowLeft,
  ShieldAlert,
} from 'lucide-react';
import { formatCurrency, formatDate, formatPhone } from '@/lib/utils';
import { ROUTES } from '@/constants/routes';
import { useCustomer, useBlockCustomer, useCustomerBookings, useCustomerPayments } from '../hooks/useCustomers';
import { useCustomerFraudReports, useCustomerBlockHistory, useUnblockCustomer } from '@/features/fraud/hooks/useFraud';
import { BlockCustomerDialog } from '@/features/fraud/components/BlockCustomerDialog';
import { ReportSeverityBadge, ReportTypeBadge } from '@/features/fraud/components/ReportSeverityBadge';
import { PageHeader } from '@/components/common/PageHeader';
import { BlockUserDialog } from '@/components/common/ConfirmDialog';
import { BookingStatusBadge } from '@/components/common/StatusBadge';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Avatar } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { PageSpinner } from '@/components/ui/spinner';
import { EmptyState } from '@/components/ui/empty-state';

export function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [showFraudBlockDialog, setShowFraudBlockDialog] = useState(false);

  const { data: customer, isLoading } = useCustomer(id!);
  const { data: bookings, isLoading: bookingsLoading } = useCustomerBookings(id!);
  const { data: payments = [], isLoading: paymentsLoading } = useCustomerPayments(id!);
  const { mutate: blockCustomer, isPending: isBlocking } = useBlockCustomer();
  const { data: fraudData, isLoading: fraudLoading } = useCustomerFraudReports(id!);
  const { data: blockHistory = [], isLoading: blocksLoading } = useCustomerBlockHistory(id!);
  const { mutate: unblockCustomer, isPending: isUnblocking } = useUnblockCustomer();

  const activeBlock = (blockHistory as any[]).find((b) => b.status === 'active');

  if (isLoading) {
    return <PageSpinner message="Loading customer details..." />;
  }

  if (!customer) {
    return (
      <EmptyState
        icon="users"
        title="Customer not found"
        description="The customer you're looking for doesn't exist or has been removed."
        action={{ label: 'Go back', onClick: () => navigate(ROUTES.CUSTOMERS) }}
      />
    );
  }

  const handleBlock = () => {
    blockCustomer(
      { id: customer.id, block: !customer.isBlocked },
      { onSuccess: () => setShowBlockDialog(false) }
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={customer.name}
        breadcrumbs={[
          { label: 'Customers', href: ROUTES.CUSTOMERS },
          { label: customer.name },
        ]}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate(ROUTES.CUSTOMERS)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <Button
              variant={customer.isBlocked ? 'success' : 'destructive'}
              onClick={() => setShowBlockDialog(true)}
            >
              {customer.isBlocked ? (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Unblock
                </>
              ) : (
                <>
                  <Ban className="h-4 w-4 mr-2" />
                  Block
                </>
              )}
            </Button>
          </div>
        }
      />

      {/* Customer Info Card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-6">
            <Avatar src={customer.avatar} alt={customer.name} size="xl" />
            <div className="flex-1 space-y-4">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-semibold">{customer.name}</h2>
                {customer.isBlocked ? (
                  <Badge variant="destructive">Blocked</Badge>
                ) : customer.isVerified ? (
                  <Badge variant="success">Verified</Badge>
                ) : (
                  <Badge variant="muted">Unverified</Badge>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Mail className="h-4 w-4" />
                  <span>{customer.email}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Phone className="h-4 w-4" />
                  <span>{formatPhone(customer.phone)}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  <span>Joined {formatDate(customer.createdAt, 'long')}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t">
                <div>
                  <p className="text-sm text-muted-foreground">Total Bookings</p>
                  <p className="text-2xl font-bold">{customer.totalBookings}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Spent</p>
                  <p className="text-2xl font-bold">{formatCurrency(customer.totalSpent)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Avg. Order Value</p>
                  <p className="text-2xl font-bold">
                    {formatCurrency(customer.totalSpent / Math.max(customer.totalBookings, 1))}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Last Active</p>
                  <p className="text-2xl font-bold">
                    {customer.lastActive ? formatDate(customer.lastActive) : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="bookings">Bookings</TabsTrigger>
          <TabsTrigger value="payments">Payments</TabsTrigger>
          <TabsTrigger value="fraud" className="flex items-center gap-1.5">
            <ShieldAlert className="h-3.5 w-3.5" />
            Fraud & Blocks
            {(fraudData?.total ?? 0) > 0 && (
              <span className="ml-1 rounded-full bg-destructive/15 text-destructive text-xs px-1.5 py-0 font-medium">
                {fraudData!.total}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Recent Bookings</CardTitle>
              </CardHeader>
              <CardContent>
                {bookingsLoading ? (
                  <div className="animate-pulse space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="h-12 bg-muted rounded" />
                    ))}
                  </div>
                ) : bookings && bookings.length > 0 ? (
                  <div className="space-y-3">
                    {bookings.slice(0, 5).map((booking) => (
                      <div
                        key={booking.id}
                        className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                      >
                        <div>
                          <p className="font-medium">{booking.bookingNumber}</p>
                          <p className="text-sm text-muted-foreground">
                            {formatDate(booking.createdAt)}
                          </p>
                        </div>
                        <BookingStatusBadge status={booking.status} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyState
                    icon="bookings"
                    title="No bookings yet"
                    description="This customer hasn't made any bookings."
                  />
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Saved Addresses</CardTitle>
              </CardHeader>
              <CardContent>
                {customer.addresses && customer.addresses.length > 0 ? (
                  <div className="space-y-3">
                    {customer.addresses.map((address, index) => (
                      <div
                        key={index}
                        className="flex items-start gap-3 p-3 rounded-lg bg-muted/50"
                      >
                        <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground" />
                        <div>
                          <p className="text-sm">{address.address}</p>
                          <p className="text-xs text-muted-foreground">
                            {address.city}, {address.state} - {address.pincode}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyState
                    icon="default"
                    title="No saved addresses"
                    description="This customer hasn't saved any addresses."
                  />
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="bookings">
          <Card>
            <CardContent className="p-6">
              {bookingsLoading ? (
                <div className="animate-pulse space-y-3">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="h-12 bg-muted rounded" />
                  ))}
                </div>
              ) : bookings && bookings.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Booking ID</TableHead>
                      <TableHead>Route</TableHead>
                      <TableHead>Vehicle</TableHead>
                      <TableHead>Fare</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bookings.map((booking) => (
                      <TableRow key={booking.id}>
                        <TableCell className="font-medium">
                          {booking.bookingNumber}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p>{booking.pickup.address.split(',')[0]}</p>
                            <p className="text-muted-foreground">
                              → {booking.dropoff.address.split(',')[0]}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell className="capitalize">
                          {(booking.vehicleType || '').replace('_', ' ') || '—'}
                        </TableCell>
                        <TableCell>{formatCurrency(booking.fare.total)}</TableCell>
                        <TableCell>
                          <BookingStatusBadge status={booking.status} />
                        </TableCell>
                        <TableCell>{formatDate(booking.createdAt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <EmptyState
                  icon="bookings"
                  title="No bookings found"
                  description="This customer hasn't made any bookings yet."
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payments">
          <Card>
            <CardContent className="p-0">
              {paymentsLoading ? (
                <div className="flex justify-center py-12"><div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" /></div>
              ) : (payments as any[]).length === 0 ? (
                <div className="p-6">
                  <EmptyState
                    icon="payments"
                    title="No payment history"
                    description="This customer hasn't made any payments yet."
                  />
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Route</TableHead>
                      <TableHead>Method</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Transaction ID</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(payments as any[]).map((p) => (
                      <TableRow key={p.id}>
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                          {formatDate(p.created_at)}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p className="truncate max-w-[160px]">{p.booking?.pickup_address?.split(',')[0] || '—'}</p>
                            <p className="text-muted-foreground truncate max-w-[160px]">→ {p.booking?.drop_address?.split(',')[0] || '—'}</p>
                          </div>
                        </TableCell>
                        <TableCell className="capitalize">{p.payment_method || '—'}</TableCell>
                        <TableCell className="text-right font-semibold">
                          {formatCurrency(parseFloat(p.amount || '0'))}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              p.payment_status === 'completed' ? 'success'
                              : p.payment_status === 'pending' ? 'warning'
                              : p.payment_status === 'failed' ? 'destructive'
                              : 'secondary'
                            }
                            className="capitalize"
                          >
                            {p.payment_status}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">
                          {p.transaction_id || p.razorpay_payment_id || p.razorpay_order_id || '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="fraud">
          <div className="space-y-6">
            {/* Active block banner */}
            {activeBlock && (
              <div className="flex items-center justify-between rounded-lg border border-destructive/30 bg-destructive/5 p-4">
                <div>
                  <p className="font-medium text-destructive">Customer is currently blocked</p>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Blocked until {formatDate(activeBlock.blocked_until)} — {activeBlock.reason}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => unblockCustomer(id!)}
                  disabled={isUnblocking}
                >
                  <CheckCircle className="h-4 w-4 mr-1.5" />
                  {isUnblocking ? 'Unblocking...' : 'Unblock Now'}
                </Button>
              </div>
            )}

            {!activeBlock && (
              <div className="flex justify-end">
                <Button variant="destructive" size="sm" onClick={() => setShowFraudBlockDialog(true)}>
                  <Ban className="h-4 w-4 mr-1.5" />
                  Block Customer
                </Button>
              </div>
            )}

            {/* Fraud reports */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Fraud Reports</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {fraudLoading ? (
                  <div className="flex justify-center py-8"><div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full" /></div>
                ) : (fraudData?.reports ?? []).length === 0 ? (
                  <div className="p-6">
                    <EmptyState icon="default" title="No fraud reports" description="No suspicious activity detected for this customer." />
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Type</TableHead>
                        <TableHead>Severity</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(fraudData?.reports ?? []).map((report) => (
                        <TableRow key={report.id}>
                          <TableCell><ReportTypeBadge type={report.report_type} /></TableCell>
                          <TableCell><ReportSeverityBadge severity={report.severity} /></TableCell>
                          <TableCell className="max-w-[200px]"><p className="text-sm truncate">{report.reason}</p></TableCell>
                          <TableCell>
                            {report.is_resolved ? <Badge variant="success">Resolved</Badge> : <Badge variant="warning">Open</Badge>}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{formatDate(report.created_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Block history */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Block History</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {blocksLoading ? (
                  <div className="flex justify-center py-8"><div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full" /></div>
                ) : (blockHistory as any[]).length === 0 ? (
                  <div className="p-6">
                    <EmptyState icon="default" title="No block history" description="This customer has never been blocked." />
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Reason</TableHead>
                        <TableHead>Duration</TableHead>
                        <TableHead>Blocked Until</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Unblock Request</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(blockHistory as any[]).map((block) => (
                        <TableRow key={block.id}>
                          <TableCell className="max-w-[160px]"><p className="text-sm truncate">{block.reason}</p></TableCell>
                          <TableCell className="text-sm">{block.duration_days}d</TableCell>
                          <TableCell className="text-sm whitespace-nowrap">{formatDate(block.blocked_until)}</TableCell>
                          <TableCell>
                            <Badge variant={block.status === 'active' ? 'destructive' : block.status === 'lifted' ? 'success' : 'secondary'} className="capitalize">
                              {block.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {block.unblock_request_status === 'none' ? '—' : (
                              <Badge variant={block.unblock_request_status === 'approved' ? 'success' : block.unblock_request_status === 'rejected' ? 'destructive' : 'warning'} className="capitalize">
                                {block.unblock_request_status}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{formatDate(block.created_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      <BlockUserDialog
        isOpen={showBlockDialog}
        onClose={() => setShowBlockDialog(false)}
        onConfirm={handleBlock}
        userName={customer.name}
        isBlocked={customer.isBlocked}
        isLoading={isBlocking}
      />

      <BlockCustomerDialog
        isOpen={showFraudBlockDialog}
        onClose={() => setShowFraudBlockDialog(false)}
        customerId={customer.id}
        customerName={customer.name}
      />
    </div>
  );
}
